// ClaimHeader.tsx — the claim workspace's identity strip (wireframe S3:
// patient/admission/payer/policy/claim chips + live TAT), following the IP
// Case Sheet's own `PatientHeader.tsx` pattern exactly (sticky, a name+
// demographics line, a pill row, status/actions on the right) rather than
// inventing a second header recipe — this is the "promote the appealing
// pattern" half of Phase 4.5's design-system pass.
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { fmtDateTime } from '@/features/ip-case-sheet/ui';
import { useAdmissionSummary, useAdmissionPayers } from './hooks';
import type { ClaimStatus, ClaimWorkspace as ClaimWorkspaceType } from './types';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', preauth_sent: 'Pre-auth sent', preauth_query: 'Pre-auth query',
  preauth_approved: 'Pre-auth approved', preauth_denied: 'Pre-auth denied',
  enhancement_sent: 'Enhancement sent', final_auth_sent: 'Final auth sent',
  final_auth_query: 'Final auth query', final_approved: 'Final approved',
  discharged: 'Discharged', submitted: 'Submitted', claim_query: 'Claim query',
  settled: 'Settled', short_settled: 'Short settled', rejected: 'Rejected',
  appealed: 'Appealed', closed: 'Closed', cancelled: 'Cancelled', documents_issued: 'Documents issued',
};
const MODE_LABEL: Record<string, string> = { cashless: 'Cashless', reimbursement: 'Reimbursement', scheme: 'Scheme', corporate: 'Corporate' };

export function statusBadgeVariant(status: ClaimStatus): 'neutral' | 'warn' | 'info' | 'success' | 'danger' {
  if (['closed', 'settled'].includes(status)) return 'success';
  if (['rejected', 'cancelled', 'preauth_denied'].includes(status)) return 'danger';
  if (['preauth_query', 'final_auth_query', 'claim_query', 'short_settled', 'appealed'].includes(status)) return 'warn';
  if (['final_approved', 'discharged', 'submitted'].includes(status)) return 'info';
  return 'neutral';
}

function tatLabel(tat: ClaimWorkspaceType['tat']): string | null {
  if (tat.authorization && !tat.authorization.decided) {
    return `${tat.authorization.kind} auth · ${tat.authorization.elapsed_min}/${tat.authorization.target_min} min`;
  }
  if (tat.query && !tat.query.replied) {
    return tat.query.hours_remaining != null ? `Query reply · ${tat.query.hours_remaining}h left` : 'Query reply overdue';
  }
  if (tat.submission_ageing && !tat.submission_ageing.breached) {
    return `Submission ageing · ${tat.submission_ageing.elapsed_days}/${tat.submission_ageing.target_days}d`;
  }
  return null;
}

export function ClaimHeader({ claim, onClose, onBack }: { claim: ClaimWorkspaceType; onClose?: () => void; onBack?: () => void }) {
  const admissionQuery = useAdmissionSummary(claim.admission_id);
  const a = admissionQuery.data;
  const coverageQuery = useAdmissionPayers(claim.admission_id);
  const coverage = (coverageQuery.data ?? [])[0];
  const tat = tatLabel(claim.tat);
  const breached = claim.tat_breached;

  // Patient identity is its own topmost line, with Back/Close at the right
  // end of that SAME line — not stacked underneath it.
  //
  // `-top-5` (not `top-0`) is the actual fix for the gap-above-the-sticky-
  // header bug, verified empirically against the live DOM before writing
  // this: App.tsx's shared page-content wrapper applies `padding: 20px 24px`
  // directly on the scrolling element itself, and a `position: sticky`
  // child's STUCK offset is resolved against that scroller's padding edge —
  // a negative `margin` does NOT move the stuck position at all (confirmed
  // live: changing margin-top left the measured gap unchanged), only the
  // `top` value does. `top: -20px` pulls the stuck position up by exactly
  // that padding, landing flush with the scroller's true border-box top —
  // content that's mid-scroll can no longer render in a gap that no longer
  // exists. `-mt-5 -mx-6` stay alongside `-top-5` so the UNSCROLLED (static,
  // pre-stick) position lands in the exact same spot — without it there'd be
  // a visible jump the instant scrolling starts. `ClaimWorkspacePanel` no
  // longer wraps this in a bordered card — a card border can't stretch to
  // cover a bled header, so the border's left for the content below it. The
  // caller (`ClaimsModule.tsx`) must not add its own padded wrapper around
  // the panel either: an extra padded ancestor between this header and
  // App.tsx's scroller clamps the bleed short of the true top (confirmed
  // live too — a second padding layer becomes the new binding constraint).
  return (
    <header className="sticky -top-5 z-20 -mx-6 -mt-5 mb-2.5 border-b border-border bg-card/95 px-6 pt-5 pb-3 backdrop-blur">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="truncate text-[15px] font-semibold">{claim.patient_name}</span>
          {a && <span className="text-xs text-ink-muted">{a.age != null ? `${a.age}y` : '—'} / {a.gender}</span>}
          <span className="text-xs text-ink-muted">{claim.uhid}</span>
          {claim.ip_number && <span className="text-xs font-semibold">{claim.ip_number}</span>}
          {a && <span className="text-xs text-ink-muted">{a.ward_name}, bed {a.bed_number}</span>}
          {a && <span className="text-xs text-ink-muted">DOA {fmtDateTime(a.admitted_at)}</span>}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {onBack && <Button variant="ghost" size="sm" onClick={onBack}>← Back to Claims desk</Button>}
          {onClose && <Button variant="ghost" size="sm" onClick={onClose}>✕ Close</Button>}
        </div>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <Badge variant="info">{MODE_LABEL[claim.mode] ?? claim.mode}</Badge>
        <Badge variant="neutral">
          {claim.payer?.name ?? claim.payer_name}
          {coverage?.tpa_payer_id && coverage.insurer_or_tpa ? ` · ${coverage.insurer_or_tpa}` : ''}
        </Badge>
        {coverage?.policy_no && <Badge variant="neutral">Policy {coverage.policy_no}</Badge>}
        <Badge variant="neutral">Claim {claim.external_claim_no ?? claim.claim_ref}</Badge>
        <Badge variant={statusBadgeVariant(claim.status)}>
          {STATUS_LABEL[claim.status] ?? claim.status}
          {tat ? ` · ${tat}` : ''}
        </Badge>
        {breached && <Badge variant="danger">TAT breached</Badge>}
      </div>
    </header>
  );
}
